import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import { API_bulk_change_permission } from "../../../../constants";
import { postRightworkAPI } from "../../requests/utilities/requests";
import ScopePicker from "./ScopePicker";
import { PERMISSION_OPTIONS } from "./constants";
import { summarizeFailureReasons, logBulkFailures } from "./bulkResults";


export default function BulkChangePermissionModal({
  open,
  onClose,
  selectedUserIds,
  companyId,
  companyStores,
  onSuccess,
  initialScope,
  initialStoreIds,
  initialPermissionType,
}) {
  const [scope, setScope] = React.useState("all");
  const [storeIds, setStoreIds] = React.useState([]);
  const [permissionType, setPermissionType] = React.useState("company_employee");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // initial* props let the drawer's per-row menu open us pre-filled.
      // Selection-based callers (toolbar) omit them and we use defaults.
      setScope(initialScope || "all");
      setStoreIds(initialStoreIds || []);
      setPermissionType(initialPermissionType || "company_employee");
    }
  }, [open, initialScope, initialStoreIds, initialPermissionType]);

  const userCount = selectedUserIds?.size || 0;
  const canSubmit =
    userCount > 0 && !submitting && (scope === "all" || storeIds.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        company_id: companyId,
        user_ids: Array.from(selectedUserIds).join(","),
        scope,
        permission_type: permissionType,
      };
      if (scope === "stores") body.store_ids = storeIds.join(",");

      const response = await postRightworkAPI(API_bulk_change_permission, body);
      const payload = response?.data?.redux?.payload || {};
      const succeeded = payload.succeeded_count || 0;
      const failed = payload.failed_count || 0;
      if (failed === 0) {
        toast.success(
          `Updated ${succeeded} membership${succeeded === 1 ? "" : "s"}.`,
          { autoClose: 3000 },
        );
      } else {
        const reasons = summarizeFailureReasons(payload.results);
        logBulkFailures("bulk_change_permission", payload.results);
        toast.warning(
          `Updated ${succeeded} of ${succeeded + failed}; ${failed} failed.${reasons ? ` Reasons: ${reasons}` : ""}`,
          { autoClose: 8000 },
        );
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Bulk permission change failed.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Change permission</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          <p className="text-sm text-slate-600">
            Updating permission for <span className="font-semibold">{userCount}</span> selected
            {userCount === 1 ? " user" : " users"}.
          </p>

          <ScopePicker
            scope={scope}
            setScope={setScope}
            storeIds={storeIds}
            setStoreIds={setStoreIds}
            companyStores={companyStores}
            radioGroupName="change-permission-scope"
          />

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              New permission
            </p>
            <Select
              value={permissionType}
              onChange={(e) => setPermissionType(e.target.value)}
              size="small"
              fullWidth
              sx={{ borderRadius: "8px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } }}
            >
              {PERMISSION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": { backgroundColor: "#5b21b6" },
              fontSize: 14,
              px: 2.5,
              py: 1,
            }}
          >
            Update permission
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
